import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import {
  useTheme,
  useMediaQuery,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';

const TransportPlaces = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canCreate = hasPermission(user?.permissions, "TransportPlaces", "create", false);
  const canEdit = hasPermission(user?.permissions, "TransportPlaces", "update", false);
  const canDelete = hasPermission(user?.permissions, "TransportPlaces", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transportPlaces, setTransportPlaces] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const fetchTransportPlaces = async () => {
      try {
        setLoading(true);
        const res = await api.get('/transportplaces');
        setTransportPlaces(res.data || []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTransportPlaces();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      sortable: false,
      filterable: false,
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "placeName",
      headerName: "Place Name",
      flex: isMobile ? undefined : 2,
      width: isMobile ? 160 : undefined,
    },
    {
      field: "address",
      headerName: "Address",
      flex: isMobile ? undefined : 3,
      width: isMobile ? 220 : undefined,
    },
    {
      field: "airportIataCode",
      headerName: "Airport",
      width: 110,
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 2,
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: "100%", height: "100%" }}
          >
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    "&:hover": { color: theme.palette.green.deep },
                  }}
                  onClick={() => navigate(`/transportplaces/${params.row._id}`)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    "&:hover": { color: theme.palette.green.deep },
                  }}
                  onClick={() => handleDeleteClick(params.row._id)}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];
  }, [navigate, theme.palette, canEdit, canDelete]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const ids = getSelectedIds(rowSelectionModel, transportPlaces);
    if (!ids.length) {
      toast.error("No rows selected");
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        await api.delete(`/transportplaces/${idToDelete}`);
        setTransportPlaces(prev => prev.filter(i => i._id !== idToDelete));
        toast.success("Transport place deleted");
      }

      if (deleteType === 'bulk') {
        const ids = getSelectedIds(rowSelectionModel, transportPlaces);
        await api.patch('/transportplaces/bulk-delete', { ids });
        setTransportPlaces(prev => prev.filter(i => !ids.includes(i._id)));
        toast.success(`${ids.length} records deleted`);
      }
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, transportPlaces]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header title="Transport Places" subtitle="List of transport places" />
        {!loading && (
          <Box display="flex" gap={1}>
            {canDelete && (
              <Tooltip title="Bulk delete">
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, transportPlaces)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create">
                <span>
                  <StyledButton onClick={() => navigate("/transportplaces/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
            {isMobile ? (
              <CardList
                rows={transportPlaces}
                primaryField="placeName"
                fields={[
                  { label: "Address", key: "address" },
                  { label: "Airport", key: "airportIataCode" },
                  { label: "Notes", key: "notes" },
                  { label: "Pickup Point", key: "pickupPoint" },
                  { label: "Dropoff Point", key: "dropoffPoint" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/transportplaces/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={transportPlaces}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  initialState: { pagination: { paginationModel: { pageSize: 50 } } },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default TransportPlaces;
